import { useState, useEffect, useCallback } from "react";
import { Link2, Copy, RefreshCw, CheckCircle, AlertCircle, Send, Loader2, FileText, Download, Archive } from "lucide-react";
import { useUser } from "@/context/UserContext";
import api from "@/utils/api";

const FINDER_API_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusLabel = ({ status }) => {
  if (!status) return null;
  const ok = status === "ok";
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
        ok ? "bg-green-500/15 text-green-300" : "bg-red-500/15 text-red-300"
      }`}
      data-testid="io-status-label"
    >
      {ok ? <CheckCircle size={12} /> : <AlertCircle size={12} />}
      {ok ? "OK" : status}
    </span>
  );
};

const formatDate = (iso) => {
  if (!iso) return "noch nie";
  try {
    return new Date(iso).toLocaleString("de-DE");
  } catch {
    return iso;
  }
};

export default function IOIntegration() {
  const { user } = useUser();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ io_webhook_url: "", io_incoming_secret: "", io_enabled: false, io_outbound_url: "", io_outbound_api_key: "" });
  const [saving, setSaving] = useState(false);
  const [generatingKey, setGeneratingKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedEndpoint, setCopiedEndpoint] = useState(false);

  const reload = useCallback(() => {
    api
      .get(`/interpreters/${user.id}/io`)
      .then((r) => {
        setSettings(r.data);
        setForm({
          io_webhook_url: r.data.io_webhook_url || "",
          io_incoming_secret: "",
          io_enabled: !!r.data.io_enabled,
          io_outbound_url: r.data.io_outbound_url || "",
          io_outbound_api_key: "",
        });
      })
      .catch(() => {});
  }, [user.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        io_webhook_url: form.io_webhook_url,
        io_enabled: form.io_enabled,
        io_outbound_url: form.io_outbound_url,
      };
      if (form.io_incoming_secret) payload.io_incoming_secret = form.io_incoming_secret;
      if (form.io_outbound_api_key) payload.io_outbound_api_key = form.io_outbound_api_key;
      await api.put(`/interpreters/${user.id}/io`, payload);
      reload();
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateKey = async () => {
    setGeneratingKey(true);
    try {
      await api.post(`/interpreters/${user.id}/io/generate-api-key`);
      reload();
    } finally {
      setGeneratingKey(false);
    }
  };

  const handleCopy = async () => {
    if (!settings?.io_api_key) return;
    try {
      await navigator.clipboard.writeText(settings.io_api_key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  const handleCopyEndpoint = async () => {
    try {
      await navigator.clipboard.writeText(FINDER_API_ENDPOINT);
      setCopiedEndpoint(true);
      setTimeout(() => setCopiedEndpoint(false), 1500);
    } catch {
      /* clipboard blocked */
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post(`/interpreters/${user.id}/io/test-webhook`);
      setTestResult(r.data);
      reload();
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || "Test fehlgeschlagen" });
    } finally {
      setTesting(false);
    }
  };

  if (!settings) return null;

  // Dark-mode (IO-style) container
  return (
    <div
      className="bg-gray-900 text-gray-100 rounded-2xl border border-gray-800 shadow-sm p-6"
      data-testid="io-integration-section"
    >
      <h2 className="font-bold text-white mb-1 flex items-center gap-2">
        <Link2 size={18} className="text-[#F5C518]" />
        Interpreter's Office Anbindung
      </h2>
      <p className="text-sm text-gray-400 mb-5 leading-relaxed">
        Verbinde diesen Account mit Interpreter's Office. Den Webhook-URL und das Incoming Secret findest du in der
        IO-App unter <span className="font-medium text-gray-200">Einstellungen → Schnittstelle</span>.
      </p>

      {/* Master toggle */}
      <div className="flex items-center justify-between gap-4 p-4 bg-gray-800/60 rounded-xl border border-gray-700 mb-5">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white">Integration aktiv</p>
          <p className="text-xs text-gray-400">Neue Anfragen werden automatisch nach IO übertragen</p>
        </div>
        <button
          type="button"
          onClick={() => setForm((prev) => ({ ...prev, io_enabled: !prev.io_enabled }))}
          data-testid="io-enabled-toggle"
          className={`relative shrink-0 w-12 h-6 rounded-full transition-colors ${
            form.io_enabled ? "bg-[#F5C518]" : "bg-gray-600"
          }`}
        >
          <span
            className={`absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
              form.io_enabled ? "translate-x-6" : "translate-x-0"
            }`}
          />
        </button>
      </div>

      {/* Finder API endpoint (to paste into IO) */}
      <div className="mb-5 p-4 bg-black/40 border border-gray-800 rounded-xl">
        <p className="text-xs font-semibold text-[#F5C518] mb-1 uppercase tracking-wide">
          ➜ In IO eintragen: API-Endpoint
        </p>
        <p className="text-xs text-gray-400 mb-2 leading-relaxed">
          Kopiere diese URL in das Feld <span className="font-semibold text-gray-200">"API-Endpoint (Interpreter-Finder)"</span> in den IO-Schnittstellen-Einstellungen.
        </p>
        <div className="flex gap-2">
          <input
            value={FINDER_API_ENDPOINT}
            readOnly
            data-testid="io-finder-endpoint"
            className="flex-1 min-w-0 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-xs text-white font-mono"
          />
          <button
            type="button"
            onClick={handleCopyEndpoint}
            data-testid="io-copy-endpoint-btn"
            className="shrink-0 px-3 py-2 bg-[#F5C518] text-gray-900 rounded-lg hover:bg-[#D7A70B] transition-colors text-xs font-semibold flex items-center gap-1"
          >
            <Copy size={14} />
            {copiedEndpoint ? "Kopiert!" : "Kopieren"}
          </button>
        </div>
      </div>

      {/* API key */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-300 mb-1">
          API-Key für IO <span className="text-gray-500 font-normal">(in IO eintragen)</span>
        </label>
        <div className="flex gap-2">
          <input
            value={settings.io_api_key || ""}
            readOnly
            placeholder="Noch nicht generiert"
            data-testid="io-api-key-display"
            className="flex-1 min-w-0 px-4 py-3 border border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-500 rounded-xl text-xs font-mono"
          />
          <button
            type="button"
            onClick={handleCopy}
            disabled={!settings.io_api_key}
            data-testid="io-copy-key-btn"
            className="shrink-0 px-3 py-3 bg-gray-800 text-gray-200 rounded-xl hover:bg-gray-700 transition-colors disabled:opacity-40"
            title="Kopieren"
          >
            <Copy size={16} />
          </button>
          <button
            type="button"
            onClick={handleGenerateKey}
            disabled={generatingKey}
            data-testid="io-generate-key-btn"
            className="shrink-0 px-4 py-3 bg-[#F5C518] text-gray-900 text-sm font-semibold rounded-xl hover:bg-[#D7A70B] transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {generatingKey ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {settings.io_api_key ? "Neu generieren" : "Generieren"}
          </button>
        </div>
        {copied && <p className="text-xs text-green-400 mt-1">Kopiert!</p>}
      </div>

      {/* Webhook URL */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-300 mb-1">IO Webhook-URL</label>
        <input
          value={form.io_webhook_url}
          onChange={(e) => setForm({ ...form, io_webhook_url: e.target.value })}
          placeholder="https://interpreters-office.de/api/integrations/interpreter-finder/webhook/..."
          data-testid="io-webhook-url-input"
          className="w-full px-4 py-3 border border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-500 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#F5C518]"
        />
      </div>

      {/* Incoming secret */}
      <div className="mb-5">
        <label className="block text-sm font-medium text-gray-300 mb-1">
          IO Incoming Secret
          {settings.io_incoming_secret_set && (
            <span className="ml-2 text-xs text-gray-500 font-normal">
              gesetzt ({settings.io_incoming_secret_masked}) — leer lassen zum Beibehalten
            </span>
          )}
        </label>
        <input
          type="password"
          value={form.io_incoming_secret}
          onChange={(e) => setForm({ ...form, io_incoming_secret: e.target.value })}
          placeholder={settings.io_incoming_secret_set ? "•••••••• (unverändert)" : "HMAC-Schlüssel aus IO-Settings"}
          data-testid="io-incoming-secret-input"
          className="w-full px-4 py-3 border border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-500 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#F5C518] font-mono"
        />
      </div>

      {/* Polling backup (read API from IO) */}
      <div className="mb-5 p-4 bg-black/40 border border-gray-800 rounded-xl">
        <p className="text-xs font-semibold text-[#F5C518] mb-2 uppercase tracking-wide">
          ➜ Optional: Polling-Backup (alle 30 s)
        </p>
        <p className="text-xs text-gray-400 mb-3 leading-relaxed">
          Falls Webhooks mal verloren gehen, fragt IF alle 30 Sekunden bei IO nach. Trage hier die <span className="font-semibold text-gray-200">Lese-API-Basis-URL</span> und einen <span className="font-semibold text-gray-200">Bearer-API-Key von IO</span> ein.
        </p>
        <label className="block text-xs font-medium text-gray-300 mb-1">IO Read-API Basis-URL</label>
        <input
          value={form.io_outbound_url}
          onChange={(e) => setForm({ ...form, io_outbound_url: e.target.value })}
          placeholder="https://interpreters-office.de/api"
          data-testid="io-outbound-url-input"
          className="w-full px-3 py-2 mb-3 border border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-500 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#F5C518] font-mono"
        />
        <label className="block text-xs font-medium text-gray-300 mb-1">
          IO Bearer-API-Key
          {settings.io_outbound_api_key_set && (
            <span className="ml-2 text-gray-500 font-normal">
              gesetzt ({settings.io_outbound_api_key_masked}) — leer lassen
            </span>
          )}
        </label>
        <input
          type="password"
          value={form.io_outbound_api_key}
          onChange={(e) => setForm({ ...form, io_outbound_api_key: e.target.value })}
          placeholder={settings.io_outbound_api_key_set ? "•••••••• (unverändert)" : "Bearer-Token aus IO"}
          data-testid="io-outbound-api-key-input"
          className="w-full px-3 py-2 border border-gray-700 bg-gray-800 text-gray-100 placeholder-gray-500 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#F5C518] font-mono"
        />
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-5">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid="io-save-btn"
          className="px-5 py-2.5 bg-white text-gray-900 text-sm font-semibold rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
        >
          {saving ? "Speichern..." : "Einstellungen speichern"}
        </button>
        <button
          type="button"
          onClick={handleTest}
          disabled={testing || !form.io_webhook_url || (!settings.io_incoming_secret_set && !form.io_incoming_secret)}
          data-testid="io-test-webhook-btn"
          className="px-5 py-2.5 bg-[#F5C518]/20 text-[#F5C518] text-sm font-semibold rounded-xl hover:bg-[#F5C518] hover:text-gray-900 transition-colors disabled:opacity-40 flex items-center gap-2"
        >
          {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
          Verbindungstest
        </button>
      </div>

      {/* Test result */}
      {testResult && (
        <div
          data-testid="io-test-result"
          className={`mb-4 px-4 py-3 rounded-xl border text-sm ${
            testResult.ok
              ? "bg-green-500/10 border-green-500/30 text-green-300"
              : "bg-red-500/10 border-red-500/30 text-red-300"
          }`}
        >
          {testResult.ok ? (
            <p className="flex items-center gap-2">
              <CheckCircle size={16} />
              Verbindungstest erfolgreich – IO hat den signierten Webhook akzeptiert.
            </p>
          ) : (
            <p className="flex items-center gap-2">
              <AlertCircle size={16} />
              Fehlgeschlagen: {testResult.error || testResult.status}
            </p>
          )}
        </div>
      )}

      {/* Status panel */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
        <div className="p-3 bg-gray-800/60 rounded-xl border border-gray-700">
          <p className="text-gray-500 mb-1">Letzter Sync aus IO</p>
          <p className="font-medium text-gray-200">{formatDate(settings.io_last_sync_at)}</p>
          <div className="flex justify-end mt-1"><StatusLabel status={settings.io_last_sync_status} /></div>
        </div>
        <div className="p-3 bg-gray-800/60 rounded-xl border border-gray-700">
          <p className="text-gray-500 mb-1">Letzter Webhook an IO</p>
          <p className="font-medium text-gray-200">{formatDate(settings.io_last_webhook_at)}</p>
          <div className="flex justify-end mt-1"><StatusLabel status={settings.io_last_webhook_status} /></div>
        </div>
        <div className="p-3 bg-gray-800/60 rounded-xl border border-gray-700">
          <p className="text-gray-500 mb-1">Letztes Polling (30 s)</p>
          <p className="font-medium text-gray-200">{formatDate(settings.io_last_poll_at)}</p>
          <div className="flex justify-end mt-1"><StatusLabel status={settings.io_last_poll_status} /></div>
        </div>
      </div>

      {/* Documentation downloads for IO team */}
      <div className="mt-5 p-4 bg-gradient-to-br from-[#14B8A6]/10 to-transparent border border-[#14B8A6]/30 rounded-xl">
        <div className="flex items-start gap-3 mb-3">
          <FileText size={18} className="text-[#5EEAD4] mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-gray-100">Dokumentation für dein IO-Team</p>
            <p className="text-xs text-gray-400 leading-relaxed mt-0.5">
              Fertige Spec + kopierbarer PHP/JS-Code zum Einbauen. Lade herunter und schicke an dein IO-Team.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <a
            href={`${FINDER_API_ENDPOINT}/io/docs-bundle`}
            download
            data-testid="io-docs-bundle-btn"
            className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-[#14B8A6] hover:bg-[#0D9488] text-gray-900 text-xs font-semibold rounded-lg transition-colors"
          >
            <Archive size={14} />
            Komplettpaket (ZIP)
          </a>
          <a
            href={`${FINDER_API_ENDPOINT}/io/docs/spec`}
            download="IO_INTEGRATION_SPEC.md"
            data-testid="io-docs-spec-btn"
            className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-100 text-xs font-medium rounded-lg border border-gray-700 transition-colors"
          >
            <Download size={14} />
            Spec (.md)
          </a>
          <a
            href={`${FINDER_API_ENDPOINT}/io/docs/code`}
            download="IO_DROPIN_CODE.md"
            data-testid="io-docs-code-btn"
            className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-100 text-xs font-medium rounded-lg border border-gray-700 transition-colors"
          >
            <Download size={14} />
            Drop-in Code (.md)
          </a>
        </div>
        <p className="text-[10px] text-gray-500 mt-2.5 leading-relaxed">
          💡 Tipp: Beide Dateien sind reines Markdown – das IO-Team kann sie direkt in VS Code, GitHub oder einem beliebigen Markdown-Viewer öffnen und Code-Blöcke kopieren.
        </p>
      </div>
    </div>
  );
}
